import { AssignmentInd, AssignmentOutlined } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { IconButton, ListItemIcon, ListItemText } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Loader from 'components/Loader';
import {
  BookCloseIcon,
  BookOpenIcon,
  DeleteIcon,
  DownloadIcon,
  DuplicateIcon,
  EditIcon,
  WorkunitsIcon,
} from 'components/CustomIcons';
import useManagementService from 'features/Management/services/useManagementService';
import useManagementRights from 'hooks/rights/useManagementRights';
import { setPrevSearchParamsAction } from 'store/actions/managementActions';
import { clearChangeBaseLineStateAction } from 'store/actions/changeBaselineActions';
import { useAppState } from 'store/Provider';
import { some } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store/hooks';
import Order from 'types/entities/Order';
import { useHistory, useLocation } from 'react-router-dom';
import './OrderMenu.scss';
import { OrderType } from 'types/enums/OrderType';
import VersionHistoryDialog from '../OrderDialogs/VersionHistoryDialog';

interface Props {
  order: Order;
}

interface LocationState {
  searchParamsPage: any;
  pathname: string;
  searchParam: string;
}

const OrderMenu: React.FC<Props> = ({ order }) => {
  const loadingState = useSelector((state) => state.loadings);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const appState = useSelector((state) => state.app);
  const open = Boolean(anchorEl);
  const {
    archiveOrder,
    openOrder,
    downloadSelectedProductionExcel,
    downloadBaselineVersionExcel,
    restoreOrder,
    navigateToSummary,
    navigateToAffectation,
    openChangeBaselineCancelDialog,
    openChangeBaselineDialog,
    openManagementDuplicationDialog,
    openManagementCloseDialog,
    openManagementOpenDialog,
    navigateToOrderCreationWorkunits,
    openEditOrderDrawer,
    openEditTimeMaterialDialog,
    getOrderBaselineVersions,
  } = useManagementService();
  const {
    canCreateWorkunitOrder,
    canShowSummaryOrder,
    canAffectationOrder,
    canBaselineOrder,
    canEditOrder,
    canDuplicateOrder,
    canDownloadOrder,
    canCloseOrder,
    canOpenOrder,
    canArchiveOrder,
    canRestoreOrder,
  } = useManagementRights(order);
  const baseLineLabel = order?.is_baselined ? 'Cancel Change Baseline' : 'Change Baseline';

  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = React.useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [t] = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { dispatch } = useAppState();
  const [versions, setVersions] = useState([]);

  const goBackManagementPage = () => {
    const name = location?.state as LocationState;
    const source_page: string | null = localStorage.getItem('from_source_page');
    if (location.pathname.includes('deliverables') && name && source_page !== 'dashboard') {
      history.push({
        pathname: name?.pathname, // Keep the current path
        search: name?.searchParam, // Keep the current query string
        state: name?.searchParamsPage, // Reset the state to null or an empty object if you prefer
      });
      dispatch(setPrevSearchParamsAction(null));
    } else if (location.pathname.includes('deliverables') && name === undefined && source_page !== 'dashboard') {
      history.goBack();
    } else {
      history.push(`/${appState.customer?.slug}/orders/production?status=production`);
    }
    localStorage.removeItem('from_source_page');
    localStorage.removeItem('forecast_date_mode');
    localStorage.removeItem('forecast_date_view');
  };

  const handleOpenEditDrawer = useCallback(() => {
    if (order.order_type_id === OrderType.TIMEANDMATERIAL) {
      openEditTimeMaterialDialog(order, 'edit');
      handleClose();
      goBackManagementPage();
    } else {
      if (location.pathname.includes('deliverables')) {
        goBackManagementPage();
      }
      openEditOrderDrawer(order);
      handleClose();
    }
  }, [order, openEditOrderDrawer, openEditTimeMaterialDialog]);

  const handleOpenDuplicateDialogPage = useCallback(() => {
    openManagementDuplicationDialog(order.id);
    handleClose();
    if (!location.pathname.includes('quotes')) {
      goBackManagementPage();
    }
  }, [order, openManagementDuplicationDialog]);

  const handleOpenCloseDialogPage = useCallback(() => {
    openManagementCloseDialog(order.id);
    handleClose();
    goBackManagementPage();
  }, [order, openManagementCloseDialog]);

  const handleReOpenDialogPage = useCallback(() => {
    openManagementOpenDialog(order.id);
    handleClose();
    if (!location.pathname.includes('quotes')) {
      goBackManagementPage();
    }
  }, [order, openManagementOpenDialog]);

  const handleRestoreDialogPage = useCallback(() => {
    restoreOrder(order.id);
    handleClose();
    if (!location.pathname.includes('quotes')) {
      goBackManagementPage();
    }
  }, [order, restoreOrder]);

  const handleDeleteDialogPage = useCallback(() => {
    archiveOrder(order.id);
    handleClose();
    if (!location.pathname.includes('quotes')) {
      goBackManagementPage();
    }
  }, [order, openManagementCloseDialog]);

  const handleNavigateToSummaryPage = useCallback(() => {
    if (order.order_type_id !== OrderType.TIMEANDMATERIAL) {
      navigateToSummary(order.id);
    }
  }, [order, navigateToSummary]);

  const handleNavigateToAffectationPage = useCallback(() => {
    if (order.order_type_id !== OrderType.TIMEANDMATERIAL) {
      navigateToAffectation(order.id);
    }
  }, [order, navigateToAffectation]);

  const handleChangeBaselineDialogPage = useCallback(() => {
    if (!order?.is_baselined) {
      dispatch(clearChangeBaseLineStateAction());
      openChangeBaselineDialog(order);
      handleClose();
      goBackManagementPage();
    } else {
      openChangeBaselineCancelDialog(order);
      handleClose();
    }
  }, [order, openChangeBaselineDialog]);

  const handleDownloadProductionExcel = useCallback(async () => {
    try {
      // const { versions: versionData } = await OrderApiService.getOrderBaselineVersions(order.id);
      const { versions: versionData } = await getOrderBaselineVersions(order.id);
      if (versionData && versionData.length > 0) {
        setVersions(versionData);
        setIsVersionHistoryOpen(true);
        handleClose();
      } else {
        downloadSelectedProductionExcel(order.id);
        handleClose();
      }
    } catch (error) {
      console.error('Failed to check baseline versions:', error);
      // Fallback to normal download if API call fails
      downloadSelectedProductionExcel(order.id);
      handleClose();
    }
  }, [order.id, downloadSelectedProductionExcel, getOrderBaselineVersions]);

  const handleCloseVersionHistory = useCallback(() => {
    setIsVersionHistoryOpen(false);
  }, []);

  // Add handler for downloading specific version from version history
  const handleDownloadVersion = useCallback(
    (versionId) => {
      downloadBaselineVersionExcel(order.id, versionId);
      // setIsVersionHistoryOpen(false);
    },
    [order.id]
  );

  const menuItems = useMemo(
    () => [
      {
        action: () => navigateToOrderCreationWorkunits(order.id, order.catalog_id),
        permission: canCreateWorkunitOrder,
        label: 'Workunits',
        icon: <WorkunitsIcon sx={{ color: 'text.primary' }} fontSize="small" />,
      },
      {
        action: handleNavigateToSummaryPage,
        permission: canShowSummaryOrder,
        label: 'Summary',
        icon: <AssignmentOutlined sx={{ color: 'text.primary' }} />,
      },
      {
        action: handleNavigateToAffectationPage,
        permission: canAffectationOrder,
        label: 'Assignment',
        icon: <AssignmentInd sx={{ color: 'text.primary' }} />,
      },
      {
        action: handleChangeBaselineDialogPage,
        permission: canBaselineOrder,
        label: baseLineLabel,
        icon: <AssignmentInd sx={{ color: 'text.primary' }} />,
      },
      {
        action: handleOpenEditDrawer,
        permission: canEditOrder,
        label: 'Edit',
        icon: <EditIcon sx={{ color: 'text.primary' }} fontSize="small" />,
      },
      {
        action: handleOpenDuplicateDialogPage,
        permission: canDuplicateOrder,
        label: 'Duplicate',
        icon: <DuplicateIcon sx={{ color: 'text.primary' }} fontSize="small" />,
      },
      {
        action: handleDownloadProductionExcel,
        permission: canDownloadOrder,
        label: 'Download Excel',
        icon: <DownloadIcon sx={{ color: 'text.primary' }} fontSize="small" />,
      },
      {
        action: handleOpenCloseDialogPage,
        permission: canCloseOrder,
        label: 'Close',
        icon: <BookCloseIcon sx={{ color: 'text.primary' }} fontSize="small" />,
      },
      {
        action: handleReOpenDialogPage,
        permission: canOpenOrder,
        label: 'Reopen',
        icon: <BookOpenIcon sx={{ color: 'text.primary' }} fontSize="small" />,
      },
      {
        action: handleDeleteDialogPage,
        permission: canArchiveOrder,
        label: 'Delete',
        color: 'error.main',
        icon: <DeleteIcon sx={{ color: 'error.main' }} fontSize="small" />,
        isLoading: (loadingState?.[`archive_${order?.id}`] ?? 0) !== 0,
      },
      {
        action: handleRestoreDialogPage,
        permission: canRestoreOrder,
        label: 'Restore',
        icon: <SettingsBackupRestoreIcon sx={{ color: 'text.primary' }} fontSize="small" />,
        isLoading: (loadingState?.[`restore_${order?.id}`] ?? 0) !== 0,
      },
    ],
    [
      canCreateWorkunitOrder,
      navigateToSummary,
      canShowSummaryOrder,
      navigateToAffectation,
      canAffectationOrder,
      handleOpenEditDrawer,
      canEditOrder,
      openManagementDuplicationDialog,
      canDuplicateOrder,
      downloadSelectedProductionExcel,
      downloadBaselineVersionExcel,
      canDownloadOrder,
      openManagementCloseDialog,
      canCloseOrder,
      openManagementOpenDialog,
      canOpenOrder,
      archiveOrder,
      canArchiveOrder,
      loadingState,
      order.id,
      order.catalog_id,
      restoreOrder,
      canRestoreOrder,
      navigateToOrderCreationWorkunits,
    ]
  );

  return some({
    canCreateWorkunitOrder,
    canShowSummaryOrder,
    canAffectationOrder,
    canEditOrder,
    canDuplicateOrder,
    canDownloadOrder,
    canCloseOrder,
    canOpenOrder,
    canArchiveOrder,
    canRestoreOrder,
  }) ? (
    <>
      <IconButton
        aria-label="more"
        aria-controls="long-menu"
        aria-haspopup="true"
        onClick={handleClick}
        size="large"
        data-testid={`menu_order_${order.id}`}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu id="long-menu" anchorEl={anchorEl} keepMounted open={open} onClose={handleClose}>
        {menuItems
          .filter((item) => item.permission)
          .map((item) => (
            <MenuItem
              key={item.label}
              onClick={() => item.action(order.id)}
              disabled={
                item.isLoading ||
                (order?.is_baselined && item.label !== 'Cancel Change Baseline' && item.label !== 'Download Excel')
              }
              sx={{ color: item.color }}
            >
              {item.isLoading ? (
                <Loader size={10} />
              ) : (
                <>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText>{t(item.label)}</ListItemText>
                </>
              )}
            </MenuItem>
          ))}
      </Menu>
      {isVersionHistoryOpen && (
        <VersionHistoryDialog
          open={isVersionHistoryOpen}
          onClose={handleCloseVersionHistory}
          orderId={order.id}
          onDownloadVersion={handleDownloadVersion}
          versions={versions}
        />
      )}
    </>
  ) : (
    <></>
  );
};
export default OrderMenu;
